import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { FEELINGS } from "@/lib/feelings";
import { TID } from "@/lib/testIds";

// Shows the 4 feeling tiles. mode = "student" | "explore".
export default function FeelingsHome({ mode = "student" }) {
  const nav = useNavigate();
  const [counts, setCounts] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const homePath = mode === "student" ? "/play" : "/explore";
  const detailBase = mode === "student" ? "/play/feelings" : "/explore/feelings";
  const endpoint = mode === "student" ? "/me/feelings" : "/public/feelings";

  React.useEffect(() => {
    api.get(endpoint)
      .then(({ data }) => {
        const map = {};
        (data.feelings || []).forEach((f) => { map[f.feeling] = f.count; });
        setCounts(map);
      })
      .finally(() => setLoading(false));
  }, [endpoint]);

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <Link to={homePath} data-testid={TID.studentBackHome} className="sw-pill h-11">
          <ArrowLeft size={16} /> Back
        </Link>
      </div>
      <header>
        <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">A gentle check-in</div>
        <h1 className="display mt-1 text-4xl md:text-6xl font-extrabold leading-tight">
          How are you feeling right now?
        </h1>
        <p className="mt-2 text-lg text-[var(--sw-ink-soft)] max-w-2xl">
          Pick the one that&apos;s closest. We&apos;ll show strategies that help.
        </p>
      </header>

      <div data-testid={TID.feelingsGrid} className="grid sm:grid-cols-2 gap-4 md:gap-6">
        {FEELINGS.map((f, i) => {
          const Icon = f.icon;
          const count = counts[f.key] ?? "";
          return (
            <motion.div
              key={f.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 * i }}
            >
              <Link
                to={`${detailBase}/${f.key}`}
                data-testid={TID.feelingTile(f.key)}
                className="block rounded-3xl border-2 p-4 pr-6 sm:p-5 sm:pr-7 md:p-7 touch-target relative overflow-hidden transition hover:shadow-lg h-full"
                style={{ background: f.bg, borderColor: f.border, minHeight: 200 }}
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl p-2 sm:p-2.5 shrink-0" style={{ background: "rgba(255,255,255,0.65)", color: f.color }}>
                    <Icon size={26} strokeWidth={1.8} className="sm:hidden" />
                    <Icon size={30} strokeWidth={1.8} className="hidden sm:block" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="display text-lg sm:text-2xl md:text-3xl font-extrabold leading-tight tracking-tight whitespace-nowrap" style={{ color: f.color }}>
                      {f.prompt}
                    </div>
                    <div className="mt-1 text-sm font-semibold" style={{ color: f.color, opacity: 0.85 }}>
                      {f.subtitle}
                    </div>
                    {loading ? null : (
                      <div className="mt-4 text-xs font-semibold uppercase tracking-wider" style={{ color: f.color, opacity: 0.7 }}>
                        {count} strateg{count === 1 ? "y" : "ies"} available
                      </div>
                    )}
                  </div>
                </div>
                <div aria-hidden className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full opacity-20"
                  style={{ background: `radial-gradient(circle at 35% 35%, #fff, ${f.color})` }}
                />
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
